import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://chatbot.internal-psel.com';

// Callbacks registered by AuthContext to bridge React state with the api module
let tokenAcquirer = null;
let onAuthFailure = null;

export const setTokenAcquirer = (fn) => { tokenAcquirer = fn; };
export const setOnAuthFailure = (fn) => { onAuthFailure = fn; };

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - acquires a fresh token via MSAL before each request
api.interceptors.request.use(
  async (config) => {
    if (tokenAcquirer) {
      try {
        const token = await tokenAcquirer();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        // Token acquisition failed - request proceeds without auth
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - notifies AuthContext on 401 instead of hard-redirecting
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      if (onAuthFailure) {
        onAuthFailure();
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (username, password) => {
    const response = await api.post('/api/admin/auth/login', {
      username,
      password,
    });
    return response.data;
  },

  verify: async () => {
    const response = await api.get('/api/admin/auth/verify');
    return response.data;
  },

  logout: () => {
    // Token management is now handled by MSAL via AuthContext
  },
};

// Enhanced Dashboard API
export const dashboardAPI = {
  getStats: async (days = 30) => {
    const response = await api.get('/api/admin/dashboard', {
      params: { days }
    });
    return response.data;
  },

  getRealtimeStats: async () => {
    const response = await api.get('/api/admin/stats/realtime');
    return response.data;
  },
};

// Enhanced Analytics API
export const analyticsAPI = {
  getConversationAnalytics: async (days = 30) => {
    const response = await api.get('/api/admin/analytics/conversations', {
      params: { days }
    });
    return response.data;
  },

  getContentAnalytics: async (days = 30) => {
    const response = await api.get('/api/admin/analytics/content', {
      params: { days }
    });
    return response.data;
  },

  getTimeSeriesData: async (metric = 'threads', days = 30) => {
    const response = await api.get('/api/admin/analytics/time-series', {
      params: { metric, days }
    });
    return response.data;
  },

  getPerformanceMetrics: async (days = 7) => {
    const response = await api.get('/api/admin/analytics/performance', {
      params: { days }
    });
    return response.data;
  },

  getEngagementMetrics: async (days = 30) => {
    const response = await api.get('/api/admin/analytics/engagement', {
      params: { days }
    });
    return response.data;
  },

  getAnalyticsSummary: async (days = 30) => {
    const response = await api.get('/api/admin/analytics/summary', {
      params: { days }
    });
    return response.data;
  },
};

// Enhanced Threads API
export const threadsAPI = {
  getThreads: async (params = {}) => {
    const response = await api.get('/api/admin/threads', { params });
    return response.data;
  },

  getThread: async (threadId) => {
    const response = await api.get(`/api/admin/threads/${threadId}`);
    return response.data;
  },

  getThreadMessages: async (threadId) => {
    const response = await api.get(`/api/admin/threads/${threadId}/messages`);
    return response.data;
  },

  deleteThread: async (threadId) => {
    const response = await api.delete(`/api/admin/threads/${threadId}`);
    return response.data;
  },

  generateSummary: async (startDate, endDate, maxThreads = 100, forceRegenerate = false) => {
    const response = await api.post('/api/admin/threads/summary', {
      start_date: startDate,
      end_date: endDate,
      max_threads: maxThreads,
      force_regenerate: forceRegenerate,
    });
    return response.data;
  },
};

// Export API
export const exportAPI = {
  exportCSV: async (params = {}) => {
    const response = await api.get('/api/admin/export/csv', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  exportJSON: async (params = {}) => {
    const response = await api.get('/api/admin/export/json', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};

// Agent Configuration API (replaces Assistant API)
export const assistantAPI = {
  // Get system info and active configuration
  getInfo: async () => {
    const response = await api.get('/api/admin/agent/info');
    return response.data;
  },

  // Get all agent configurations
  getConfigs: async () => {
    const response = await api.get('/api/admin/agent/configs');
    return response.data;
  },

  // Get active configuration
  getActiveConfig: async () => {
    const response = await api.get('/api/admin/agent/config/active');
    return response.data;
  },

  // Get specific configuration
  getConfig: async (configId) => {
    const response = await api.get(`/api/admin/agent/config/${configId}`);
    return response.data;
  },

  // Create new configuration
  createConfig: async (config) => {
    const response = await api.post('/api/admin/agent/config', config);
    return response.data;
  },

  // Update existing configuration
  updateConfig: async (configId, config) => {
    const response = await api.put(`/api/admin/agent/config/${configId}`, config);
    return response.data;
  },

  // Activate a configuration
  activateConfig: async (configId) => {
    const response = await api.post(`/api/admin/agent/config/${configId}/activate`);
    return response.data;
  },

  // Delete configuration
  deleteConfig: async (configId) => {
    const response = await api.delete(`/api/admin/agent/config/${configId}`);
    return response.data;
  },

  // Get available models
  getModels: async () => {
    const response = await api.get('/api/admin/agent/models');
    return response.data;
  },

  // Vector store file management
  getFiles: async () => {
    const response = await api.get('/api/admin/agent/vector-store/files');
    return response.data;
  },

  getFileContent: async (fileId) => {
    const response = await api.get(`/api/admin/agent/vector-store/files/${fileId}/content`);
    return response.data;
  },

  uploadFiles: async (files) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    const response = await api.post('/api/admin/agent/vector-store/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteFile: async (fileId, vectorStoreId) => {
    const formData = new FormData();
    formData.append('vector_store_id', vectorStoreId);
    
    const response = await api.delete(`/api/admin/agent/vector-store/files/${fileId}`, {
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Utility functions for API calls
export const apiUtils = {
  // Handle API errors consistently
  handleError: (error) => {
    if (error.response) {
      // Server responded with error status
      const message = error.response.data?.detail || error.response.data?.message || 'An error occurred';
      throw new Error(message);
    } else if (error.request) {
      // Request was made but no response received
      throw new Error('Network error - please check your connection');
    } else {
      // Something else happened
      throw new Error(error.message || 'An unexpected error occurred');
    }
  },

  // Format API responses consistently
  formatResponse: (response) => {
    return response.data || response;
  },

  // Create download link for blob responses
  downloadBlob: (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  // Format date for API requests
  formatDateForAPI: (date) => {
    if (!date) return null;
    return date instanceof Date ? date.toISOString() : date;
  },

  // Parse API date responses
  parseAPIDate: (dateString) => {
    if (!dateString) return null;
    return new Date(dateString);
  },
};

export default api;